import { Router } from '@angular/router';
import { PartyContactMechanisms } from '../../../model/school.model';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, UntypedFormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { PARTYROLE } from 'src/app/model/region.model';
import { AppState } from 'src/app/reducers';
import { User } from '../../auth/model/user.model';
import { Region } from '../../../model/region.model';
import { MsgModel } from '../../common-components/msg/model/msg.model';
import { UserProfileService } from '../service/user-profile.service';
import { AuthActions } from '../../auth/state/action-types';
import { Observable, filter, debounceTime, map, Subject, Subscription, takeUntil } from 'rxjs';

@Component({
    selector: 'edit-user-profile',
    templateUrl: './edit-user-profile.component.html',
    styleUrls: ['./edit-user-profile.component.scss']
})
export class EditUserProfileComponent implements OnInit, OnDestroy {
    personalInfoForm!: UntypedFormGroup;

    user: User = {};
    partyRoles: PARTYROLE[] = [];
    regions: Region[] = [];
    userIdChanged: boolean = false;

    partyContactMechanisms: PartyContactMechanisms[] = [];
    deletePartyContactMechanisms: PartyContactMechanisms[] = [];
    isEditingPartyContactMechanisms: boolean = false;

    suffixes: string[] = ['Jr', 'Sr', 'II', 'III', 'IV', 'V', 'VI'];

    isLoading: boolean = false;
    isDone: boolean = false;
    msg: MsgModel = {};
    userIdChangedMsg: MsgModel = {};

    promises: Array<any> = [];

    myControl: any;
    filteredOptions!: Observable<any>;
    private unsubUsername$: Subject<void> = new Subject<void>();

    constructor(private fb: UntypedFormBuilder, private userProfileService: UserProfileService, private store: Store<AppState>, private router: Router) {
        this.personalInfoForm = fb.group({
            firstName: ['', [Validators.required]],
            middleInitial: ['', null],
            lastName: ['', [Validators.required]],
            suffix: ['', null],
            workTitle: ['', [Validators.required]],
            workEmail: ['', [Validators.required, Validators.email]],
            username: ['', [Validators.required]]
        });
    }

    ngOnInit(): void {
        this.getUserProfile();
        Promise.all(this.promises).then((res) => {
            this.getPartyRoles();
            this.getRegionsByPartyId();
        });

        this.myControl = <UntypedFormControl>this.personalInfoForm.get('username');

        this.filteredOptions = this.myControl.valueChanges.pipe(takeUntil(this.unsubUsername$)).subscribe((value: string) => {
            let editUser: User = JSON.parse(JSON.stringify(this.user));

            if (editUser.USER_ID !== value) {
                this.userIdChangedMsg.MSG = '* Your username will be updated. You will be required to login again.';
            } else {
                this.userIdChangedMsg = {};
            }
        });
    }

    ngOnDestroy(): void {
        this.unsubUsername$.next();
        this.unsubUsername$.complete();
    }

    getUserProfile() {
        let deferred = new Promise((resolve, reject) => {
            this.userProfileService.getMyProfile().subscribe((res) => {
                if (res?.user) {
                    this.user = res.user;

                    this.personalInfoForm.patchValue({
                        firstName: this.user.PERSON?.FIRST_NAME,
                        middleInitial: this.user.PERSON?.MIDDLE_INITIAL,
                        lastName: this.user.PERSON?.LAST_NAME,
                        suffix: this.user.PERSON?.SUFFIX,
                        workTitle: this.user.WORK_TITLE,
                        workEmail: this.user.EMAIL_ADDRESS,
                        username: this.user.USER_ID
                    });

                    resolve(res);
                } else if (res?.msg) {
                    reject(res);
                } else {
                    reject('err');
                }
            });
        });

        this.promises.push(deferred);
    }

    getPartyRoles() {
        this.userProfileService.getPartyRoles(this.user!.PERSON!.PARTy!.ID!).subscribe((res) => {
            if (res?.partyRoles) {
                this.partyRoles = res.partyRoles;
            }
        });
    }

    getRegionsByPartyId() {
        this.userProfileService.getRegionsByPartyId(this.user!.PERSON!.PARTy!.ID!).subscribe((res) => {
            if (res?.regions) {
                this.regions = res.regions;
            }
        });
    }

    submit() {
        this.isLoading = true;

        this.userProfileService
            .updateMyContactInfo(this.buildEditUser(), this.buildPartyContactMechsnisms(), this.buildDeletePartyContactMechanisms())
            .subscribe({
                next: (res) => {
                    if (res.msg) {
                        if (res.msg.SUCCESS) {
                            this.msg = res.msg;
                            this.isLoading = false;
                            this.isDone = true;

                            if (this.userIdChanged) {
                                this.msg.MSG += ' You have changed your username, logging out in 5 seconds.';
                                setTimeout(() => {
                                    this.store.dispatch(AuthActions.logout());
                                    this.router.navigateByUrl('/login');
                                }, 5000);
                            } else {
                                this.msg.MSG += ' Returning to User Profile in 3 seconds.';
                                setTimeout(() => {
                                    this.router.navigateByUrl('/account/user-profile');
                                }, 3000);
                            }
                        } else {
                            this.msg = res.msg;
                            this.isLoading = false;
                        }
                    } else {
                        this.msg = { SUCCESS: false, MSG: 'Error connecting to server.' };
                        this.isLoading = false;
                    }
                },
                error: (err) => {
                    this.msg = { SUCCESS: false, MSG: 'Server Error.' };
                    this.isLoading = false;
                }
            });
    }

    buildEditUser() {
        let editUser: User = JSON.parse(JSON.stringify(this.user));

        this.userIdChanged = editUser.USER_ID !== this.personalInfoForm.get('username')?.value;

        editUser.PERSON!.FIRST_NAME = this.personalInfoForm.get('firstName')?.value;
        editUser.PERSON!.MIDDLE_INITIAL = this.personalInfoForm.get('middleInitial')?.value;
        editUser.PERSON!.LAST_NAME = this.personalInfoForm.get('lastName')?.value;
        editUser.PERSON!.SUFFIX = this.personalInfoForm.get('suffix')?.value;
        editUser.WORK_TITLE! = this.personalInfoForm.get('workTitle')?.value;
        editUser.EMAIL_ADDRESS! = this.personalInfoForm.get('workEmail')?.value;
        editUser.USER_ID! = this.personalInfoForm.get('username')?.value;

        return editUser;
    }

    buildPartyContactMechsnisms() {
        let partyContactMechsnisms: PartyContactMechanisms[] = [];

        partyContactMechsnisms = partyContactMechsnisms.concat(this.partyContactMechanisms);

        return partyContactMechsnisms;
    }

    buildDeletePartyContactMechanisms() {
        let deletePartyContactMechsnisms: PartyContactMechanisms[] = [];

        deletePartyContactMechsnisms = deletePartyContactMechsnisms.concat(this.deletePartyContactMechanisms);

        return deletePartyContactMechsnisms;
    }

    //edit-contact-mechanisms functions
    partyContactMechanismsChange(update: any) {
        this.partyContactMechanisms = update.partyContactMechanisms;
        this.deletePartyContactMechanisms = update.deletePartyContactMechanisms;

        console.log('partyContactMechanismsChange', update);
    }
}
